import * as React from "react";
import defaultSRC from "assets/images/default-project-img.svg";
import type { Image } from "../types";
import { ImgBackground } from "./FeaturedImage";
import { Img } from "./Img";
import { NSFWOverlay } from "./NSFWOverlay";
import { isTaggedNSFW } from "../utils/nsfw";

type ThumbnailProps = Omit<React.ComponentProps<typeof Img>, "src"> & {
  src?: string | Image;
};

const createImgAttributes = (src?: string | Image) => {
  if (!src) {
    return { src: defaultSRC };
  }
  if (typeof src === "string") {
    return { src };
  }

  let srcSet: string | undefined;
  if (src.url150 || src.url300 || src.url600) {
    srcSet = [
      src.url150 ? `${src.url150} 150w` : null,
      src.url300 ? `${src.url300} 300w` : null,
      src.url600 ? `${src.url600} 600w` : null,
    ]
      .filter(Boolean)
      .join(", ");
  }

  return {
    src: src.url300 ?? src.url,
    srcSet,
    sizes: srcSet ? "(min-width: 800px) 25vw, 96vw" : undefined,
  };
};

/**
 * ReviewableCard thumbnail image component
 */
function Thumbnail({ src, className, entityType, ...props }: ThumbnailProps) {
  const [isError, setIsError] = React.useState(false);

  // Build <img> attributes depending on `src` type
  const attrs = createImgAttributes(src);

  // If optimized src doesn't exist for whatever reason, fallback to original src
  const onError: React.ReactEventHandler<HTMLImageElement> = () => {
    // To prevent endless error loop
    if (!isError) setIsError(true);
  };

  const isNSFW = isTaggedNSFW(src);

  return (
    <ImgBackground
      entityType={entityType}
      blur={typeof src === "object" ? src.blur : undefined}
      className={className}
    >
      {isNSFW && <NSFWOverlay />}

      <Img
        entityType={entityType}
        onError={onError}
        {...attrs}
        src={isError ? defaultSRC : attrs.src}
        {...props}
      />
    </ImgBackground>
  );
}

export { Thumbnail };
